import React, { useState } from 'react';
import { PiFileLight, PiScalesLight, PiBookOpenLight, PiArrowSquareOutLight } from 'react-icons/pi';
import Thumbnail from '../../Settings/AIStudio/Thumbnail';

interface ExternalURLPreviewProps {
  url?: string;
  title: string;
  contentType?: string;
  relevanceScore?: number;
  justification?: string;
  summary?: string;
  type?: 'law' | 'case' | 'document';
}

const ExternalURLPreview: React.FC<ExternalURLPreviewProps> = ({
  url,
  title,
  contentType,
  relevanceScore,
  justification,
  summary,
  type = 'document',
}) => {
  const [isHovered, setIsHovered] = useState(false);

  // Get icon based on type
  const getIcon = () => {
    switch (type) {
      case 'law':
        return <PiScalesLight className="h-8 w-8 text-blue-500" />;
      case 'case':
        return <PiBookOpenLight className="h-8 w-8 text-gray-600" />;
      default:
        return <PiFileLight className="h-8 w-8 text-purple-600" />;
    }
  };

  const handleOpenLink = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div
      className="w-full h-full relative rounded-lg overflow-hidden group cursor-pointer"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleOpenLink}
    >
      {/* Thumbnail preview of the first page */}
      {url && (
        <Thumbnail
          url={url}
          name={title}
          contentType={contentType}
          className="w-full h-full"
          fallbackIcon={getIcon()}
          preferPdfPreview={true}
        />
      )}

      {/* Fallback if no URL */}
      {!url && (
        <>
          <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700/30 dark:to-gray-800/30 group-hover:from-gray-100 group-hover:to-gray-200 dark:group-hover:from-gray-700/50 dark:group-hover:to-gray-800/50 transition-all duration-300" />
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-gray-600 dark:text-gray-400">
            {getIcon()}
          </div>
        </>
      )}

      {/* Hover overlay with justification and summary */}
      {isHovered && (justification || summary) && (
        <div className="absolute inset-0 bg-black/70 dark:bg-black/80 backdrop-blur-sm z-20 flex flex-col items-center justify-center p-4 rounded-lg animate-fade-in">
          <div className="text-center max-h-full overflow-y-auto">
            {justification && (
              <p className="text-xs text-white/90 leading-relaxed mb-2 font-medium">
                {justification}
              </p>
            )}
            {summary && (
              <p className="text-xs text-white/70 leading-relaxed line-clamp-4 italic">
                {summary}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Relevance score badge */}
      {relevanceScore && (
        <div className="absolute top-2 right-2 z-20">
          <div className="px-2 py-1 bg-green-600/90 dark:bg-green-700/90 text-white rounded text-xs font-bold backdrop-blur-sm group-hover:bg-green-700 dark:group-hover:bg-green-600 transition-colors duration-300 shadow-lg">
            {Math.round(relevanceScore * 100)}%
          </div>
        </div>
      )}

      {/* Subtle border and shadow on hover */}
      <div className="absolute inset-0 rounded-lg border border-gray-300 dark:border-gray-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none group-hover:shadow-[0_0_15px_rgba(0,0,0,0.1)] dark:group-hover:shadow-[0_0_15px_rgba(0,0,0,0.3)]" />
    </div>
  );
};

export default ExternalURLPreview;
